import React, { useEffect, useMemo, useRef, useState } from 'react';

interface ImageItem {
  id: string;
  url: string;
}

interface ImageLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Page8Props {
  isPDF?: boolean;
  unusedImages?: ImageItem[];
  currentPage?: number;
  totalPages?: number;
  selectedImages?: ImageItem[];
  imageLayouts?: Record<string, ImageLayout>;
  onImageLayoutChange?: (imageId: string, layout: ImageLayout) => void;
  onCropImage?: (imageId: string) => void;
}

const IMAGES_PER_PAGE = 2;
const AREA_WIDTH = 502;
const AREA_HEIGHT = 602;
const MIN_W = 120;
const MIN_H = 120;

const Page8: React.FC<Page8Props> = ({
  isPDF = false,
  unusedImages = [],
  currentPage = 1,
  selectedImages = [],
  imageLayouts = {},
  onImageLayoutChange,
  onCropImage
}) => {
  const startIndex = (currentPage - 1) * IMAGES_PER_PAGE;
  const pageImages = unusedImages.slice(startIndex, startIndex + IMAGES_PER_PAGE);
  const selectedIdSet = new Set((selectedImages || []).map((img) => img.id));
  const areaRef = useRef<HTMLDivElement>(null);

  const [localLayouts, setLocalLayouts] = useState<Record<string, ImageLayout>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  const [resizeId, setResizeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const defaultLayouts = useMemo(() => {
    const layouts: Record<string, ImageLayout> = {};
    pageImages.forEach((img, idx) => {
      if (pageImages.length === 1) {
        layouts[img.id] = { x: 40, y: 15, width: 422, height: 572 };
      } else {
        layouts[img.id] = { x: 40, y: idx === 0 ? 10 : 306, width: 422, height: 286 };
      }
    });
    return layouts;
  }, [pageImages]);

  useEffect(() => {
    setLocalLayouts((prev) => ({ ...prev, ...imageLayouts }));
  }, [imageLayouts]);

  const clamp = (layout: ImageLayout): ImageLayout => {
    const w = Math.max(MIN_W, Math.min(AREA_WIDTH, layout.width));
    const h = Math.max(MIN_H, Math.min(AREA_HEIGHT, layout.height));
    return {
      width: w,
      height: h,
      x: Math.max(0, Math.min(AREA_WIDTH - w, layout.x)),
      y: Math.max(0, Math.min(AREA_HEIGHT - h, layout.y))
    };
  };

  const getLayout = (id: string): ImageLayout =>
    clamp(localLayouts[id] || imageLayouts[id] || defaultLayouts[id] || { x: 40, y: 10, width: 422, height: 286 });

  useEffect(() => {
    if (!dragId && !resizeId) return;

    const onMove = (e: MouseEvent) => {
      const rect = areaRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (dragId) {
        const current = getLayout(dragId);
        const next = clamp({
          ...current,
          x: e.clientX - rect.left - dragOffset.x,
          y: e.clientY - rect.top - dragOffset.y
        });
        setLocalLayouts((prev) => ({ ...prev, [dragId]: next }));
      } else if (resizeId) {
        const current = getLayout(resizeId);
        const next = clamp({
          ...current,
          width: resizeStart.w + (e.clientX - resizeStart.x),
          height: resizeStart.h + (e.clientY - resizeStart.y)
        });
        setLocalLayouts((prev) => ({ ...prev, [resizeId]: next }));
      }
    };

    const onUp = () => {
      const activeId = dragId || resizeId;
      if (activeId) onImageLayoutChange?.(activeId, getLayout(activeId));
      setDragId(null);
      setResizeId(null);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [dragId, resizeId, dragOffset, resizeStart, localLayouts, imageLayouts, defaultLayouts, onImageLayoutChange]);

  const startDrag = (e: React.MouseEvent, id: string) => {
    if (isPDF) return;
    e.preventDefault();
    const rect = areaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const layout = getLayout(id);
    setDragId(id);
    setDragOffset({ x: e.clientX - rect.left - layout.x, y: e.clientY - rect.top - layout.y });
  };

  const startResize = (e: React.MouseEvent, id: string) => {
    if (isPDF) return;
    e.preventDefault();
    e.stopPropagation();
    const layout = getLayout(id);
    setResizeId(id);
    setResizeStart({ x: e.clientX, y: e.clientY, w: layout.width, h: layout.height });
  };

  if (isPDF && pageImages.length === 0) return null;

  const shellClass = isPDF
    ? 'relative w-[546px] h-[790px] top-[26px]'
    : 'relative w-[546px] h-[790px] top-[26px] left-[25px]';

  return (
    <div
      className={isPDF ? '' : 'bg-white grid justify-items-center [align-items:start] w-screen'}
      style={isPDF ? { width: '595px', height: '842px', backgroundColor: 'white' } : undefined}
    >
      <div className={isPDF ? '' : 'bg-white w-[595px] h-[842px]'} style={isPDF ? { width: '595px', height: '842px' } : undefined}>
        <div className={shellClass} style={isPDF ? { left: '24.5px' } : undefined}>
          <div className="absolute w-[393px] h-[106px] top-[25px] left-[77px]">
            <div className="absolute w-[391px] h-[54px] top-[52px] left-0 bg-black rounded-[36px] border-[6px] border-solid border-[#722420]">
              <div className="absolute w-[364px] top-2 left-2 [font-family:'Inter-Bold',Helvetica] font-bold text-white text-[21px] text-center tracking-[0.42px] leading-[normal] whitespace-nowrap">
                Inspection Images
              </div>
            </div>
            <img className="absolute w-[124px] h-[52px] top-0 left-[134px] object-cover" alt="Logo" src="/logo.webp" />
          </div>

          <div className="absolute w-[546px] h-[790px] top-0 left-0 border-2 border-solid border-[#722420] pointer-events-none">
            <div className="relative w-[536px] h-[780px] top-[3px] left-[3px] border-2 border-solid border-[#722420]" />
          </div>

          <div className="absolute left-[22px] top-[146px] w-[502px] h-[602px] bg-white border pointer-events-none" />

          <div ref={areaRef} className="absolute left-[22px] top-[146px] w-[502px] h-[602px] overflow-hidden z-20">
            {pageImages.map((image, idx) => {
              const layout = getLayout(image.id);
              return (
                <div
                  key={image.id}
                  className="absolute border border-[#722420] bg-white overflow-hidden"
                  style={{
                    left: `${layout.x}px`,
                    top: `${layout.y}px`,
                    width: `${layout.width}px`,
                    height: `${layout.height}px`,
                    cursor: dragId === image.id ? 'grabbing' : (isPDF ? 'default' : 'grab'),
                    zIndex: dragId === image.id || resizeId === image.id ? 30 : idx + 1,
                    userSelect: 'none'
                  }}
                  onMouseDown={(e) => startDrag(e, image.id)}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <img src={image.url} alt={`Inspection image ${startIndex + idx + 1}`} className="w-full h-full object-cover pointer-events-none" />

                  {selectedIdSet.has(image.id) && (
                    <div className="absolute bottom-1 left-1 bg-black text-white text-[12px] font-bold px-1.5 py-0.5 rounded">
                      Invoice
                    </div>
                  )}

                  {!isPDF && (
                    <button
                      type="button"
                      className="absolute top-2 right-2 z-40 bg-[#722420] hover:bg-[#5a1d1a] text-white text-[11px] rounded px-2 py-1"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCropImage?.(image.id);
                      }}
                    >
                      Crop
                    </button>
                  )}

                  {!isPDF && (
                    <div
                      className="absolute -bottom-2 -right-2 w-5 h-5 rounded-full bg-[#722420] border-2 border-white cursor-nwse-resize z-40"
                      onMouseDown={(e) => startResize(e, image.id)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Page8;
